import React, { useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import Table from "../../../components/Table";
import Input from "../../../components/Input";
import InputDate from "../../../components/InputDate";
import Icon from "../../../components/Icon";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { canUseGestao, getGestaoState, hasPermission } from "../../../services/auth";
import { GestaoTeaserInline } from "../../../components/GestaoTeaser";
import { convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import { getEntryStatusLabel } from "../../Finance/helpers";
import { getKindLabel, isPurchaseOrderCanceled } from "../../PurchaseOrder/helpers";
import { StatusPill } from "../../PurchaseOrder/PurchaseOrderStatus";

/**
 * Financeiro e compras do pedido (BDM-887).
 *
 * A geração de títulos e de pedidos de compra deixou de ser automática em
 * 08/08 — criar cobrança e comprar de fornecedor são decisões, não efeitos
 * colaterais de salvar um pedido. Só que a decisão ficou sem caminho: os
 * endpoints existiam e nenhuma tela os chamava.
 *
 * Esta aba é esse caminho. Mostra o que o pedido já gerou e oferece gerar o
 * que falta, sempre com confirmação e com as parcelas à vista antes de
 * criar — a condição de pagamento do pedido é só a sugestão inicial.
 */
const OrderFinanceTab = ({ order, purchaseOrdersVersion, onOpenPurchaseOrder, onPurchaseOrdersGenerated }) => {
    const [loadingEntries, setLoadingEntries] = useState(false);
    const [loadingPurchases, setLoadingPurchases] = useState(false);
    const [generating, setGenerating] = useState(false);

    const [entries, setEntries] = useState([]);
    const [purchaseOrders, setPurchaseOrders] = useState([]);

    // Modal de parcelas: nasce com a sugestão do backend (interpretação do
    // payment_term) e é editável linha a linha antes de criar.
    const [showInstallments, setShowInstallments] = useState(false);
    const [installments, setInstallments] = useState([]);
    const [suggestion, setSuggestion] = useState(null);
    const [loadingSuggestion, setLoadingSuggestion] = useState(false);

    // Por que a automação não gerou os títulos (condição em texto livre ou
    // ausente) — aparece no bloco em vez de a falha passar calada (BDM-1208).
    const [pendingReason, setPendingReason] = useState(null);

    const navigate = useNavigate();

    // As listas dos módulos aceitam o recorte por pedido na URL; o código vai
    // junto só para o aviso da tela conseguir nomear o pedido.
    const orderScope = () =>
        `order_id=${order.id}${order.code ? `&order_code=${encodeURIComponent(order.code)}` : ''}`;
    const financeListUrl = () => `/finance/receivables?${orderScope()}`;
    const purchaseListUrl = () => `/purchase-order/list?${orderScope()}`;

    const hasFinanceFeature = canUseGestao();
    const canViewFinance = hasPermission(['view_finance', 'manage_finance']);
    const canManageFinance = hasPermission('manage_finance');
    const canViewPurchase = hasPermission(['view_purchase_order', 'manage_purchase_order']);
    const canManagePurchase = hasPermission('manage_purchase_order');

    useEffect(() => {
        if (!order?.id || !hasFinanceFeature) return;

        if (canViewFinance) getEntries();
        if (canManageFinance) getPendingReason();
    }, [order?.id]);

    useEffect(() => {
        if (!order?.id || !hasFinanceFeature || !canViewPurchase) return;

        getPurchaseOrders();
    }, [order?.id, purchaseOrdersVersion]);

    const getPendingReason = () => {
        api.get(`/finance-entry/order/${order.id}/suggest`)
            .then(res => setPendingReason(res.data.resource.pending_reason))
            .catch(() => setPendingReason(null));
    };

    const getEntries = () => {
        setLoadingEntries(true);

        api.get(`/finance-entry/paginate?order_id=${order.id}&per_page=100`).then(res => {
            setEntries(res.data.resources.data ?? []);
        }).catch(renderError).then(() => setLoadingEntries(false));
    };

    const getPurchaseOrders = () => {
        setLoadingPurchases(true);

        api.get(`/purchase-order/paginate?order_id=${order.id}&per_page=100`).then(res => {
            setPurchaseOrders(res.data.resources.data ?? []);
        }).catch(renderError).then(() => setLoadingPurchases(false));
    };

    // Títulos cancelados não contam como "já gerado": é o mesmo critério do
    // backend, que libera a geração quando sobrou só cancelado.
    const activeEntries = entries.filter(entry => entry.status !== 'canceled');
    const activePurchaseOrders = purchaseOrders.filter(purchase => !isPurchaseOrderCanceled(purchase));

    const openInstallments = () => {
        setShowInstallments(true);
        setLoadingSuggestion(true);

        api.get(`/finance-entry/order/${order.id}/suggest`).then(res => {
            const resource = res.data.resource;

            setSuggestion(resource);
            setInstallments(
                (resource.installments ?? []).map((installment, index) => ({
                    key: `${index}`,
                    amount: convertIntToMoney(installment.amount, true),
                    due_date: installment.due_date,
                }))
            );

            // Condição de pagamento que o sistema não sabe ler (texto livre
            // como "a combinar") não impede nada — só começa em uma parcela
            // à vista, para o usuário ajustar.
            if (!resource.parseable) {
                setInstallments([{
                    key: '0',
                    amount: convertIntToMoney(resource.total, true),
                    due_date: moment().format('YYYY-MM-DD'),
                }]);
            }
        }).catch(renderError).then(() => setLoadingSuggestion(false));
    };

    const updateInstallment = (key, changes) => setInstallments(current =>
        current.map(installment => installment.key === key ? { ...installment, ...changes } : installment)
    );

    const addInstallment = () => setInstallments(current => [
        ...current,
        {
            key: `${Date.now()}`,
            amount: convertIntToMoney(0, true),
            // Um mês depois da última — o passo mais comum, e editável.
            due_date: moment(current[current.length - 1]?.due_date ?? undefined)
                .add(30, 'days')
                .format('YYYY-MM-DD'),
        },
    ]);

    const removeInstallment = key =>
        setInstallments(current => current.filter(installment => installment.key !== key));

    const installmentsTotal = installments.reduce(
        (sum, installment) => sum + (convertMoenyToInt(installment.amount) || 0),
        0
    );
    const difference = installmentsTotal - (suggestion?.total ?? 0);

    const generateEntries = () => {
        if (installments.length === 0) {
            toast.error('Informe ao menos uma parcela.');
            return;
        }

        if (installments.some(installment => !installment.due_date)) {
            toast.error('Toda parcela precisa de uma data de vencimento.');
            return;
        }

        if (installments.some(installment => (convertMoenyToInt(installment.amount) || 0) <= 0)) {
            toast.error('Toda parcela precisa de um valor maior que zero.');
            return;
        }

        setGenerating(true);

        api.post(`/finance-entry/from-order`, {
            order_id: order.id,
            installments: installments.map(installment => ({
                amount: convertMoenyToInt(installment.amount),
                due_date: installment.due_date,
            })),
        }).then(res => {
            const created = (res.data.resources ?? []).length;

            toast.success(`${created} ${created === 1 ? 'título gerado' : 'títulos gerados'} a partir do pedido.`);
            setShowInstallments(false);
            getEntries();
        }).catch(renderError).then(() => setGenerating(false));
    };

    const generatePurchaseOrders = () => {
        const confirmed = window.confirm(
            'Gerar os pedidos de compra deste pedido? Sai um para cada fornecedor dos itens.'
        );

        if (!confirmed) return;

        setGenerating(true);

        api.post(`/purchase-order/from-order/${order.id}`).then(res => {
            const created = (res.data.resources ?? []).length;

            toast.success(
                `${created} ${created === 1 ? 'pedido de compra gerado' : 'pedidos de compra gerados'}.`
            );
            getPurchaseOrders();
            onPurchaseOrdersGenerated();
        }).catch(renderError).then(() => setGenerating(false));
    };

    const entryRows = entries.map(entry => ({
        id: entry.id,
        data: [
            entry.installment_count > 1
                ? `${entry.installment_number}/${entry.installment_count}`
                : '—',
            entry.description,
            entry.due_date ? moment(entry.due_date).format('DD/MM/YYYY') : '—',
            convertIntToMoney(entry.amount, true),
            getEntryStatusLabel(entry.status),
        ],
        action: () => navigate(financeListUrl()),
    }));

    const purchaseRows = purchaseOrders.map(purchase => ({
        id: purchase.id,
        data: [
            purchase.code ?? '—',
            purchase.company_trade_name ?? purchase.company_company_name ?? '—',
            getKindLabel(purchase.kind),
            convertIntToMoney(purchase.total, true),
            <StatusPill status={purchase.status} />,
        ],
        action: () => onOpenPurchaseOrder(purchase),
    }));

    if (!order?.id) {
        return <div className={`order-billing`} />;
    }

    if (!hasFinanceFeature) {
        return (
            <div className={`order-billing`}>
                <GestaoTeaserInline
                    state={getGestaoState()}
                    title={`Financeiro e compras do pedido`}
                    text={`Títulos a receber e a pagar gerados pela condição de pagamento, e pedidos de compra por fornecedor, tudo ligado a este pedido.`}
                />
            </div>
        );
    }

    return (
        <>
            <Modal
                show={showInstallments}
                close={() => setShowInstallments(false)}
                title={`Gerar títulos do pedido`}
                footer={
                    <>
                        <Button
                            type={`primary`}
                            size={`small`}
                            svg={`save-white`}
                            text={`Gerar ${installments.length} ${installments.length === 1 ? 'título' : 'títulos'}`}
                            action={generateEntries}
                            loading={generating}
                        />
                        <Button
                            type={`secondary`}
                            size={`small`}
                            text={`Cancelar`}
                            action={() => setShowInstallments(false)}
                        />
                    </>
                }
            >
                <div className="section">
                    {loadingSuggestion &&
                        <p className={`order-billing-hint`}>Carregando as parcelas sugeridas...</p>
                    }

                    {!loadingSuggestion && suggestion &&
                        <>
                            <p className={`order-billing-hint`}>
                                {suggestion.parseable
                                    ? <>Parcelas sugeridas pela condição de pagamento do pedido
                                        (<strong>{suggestion.payment_term}</strong>). Ajuste o que precisar
                                        antes de gerar.</>
                                    : <>{suggestion.pending_reason} Ou informe as parcelas abaixo.</>
                                }
                                {suggestion.payment_method && <> Forma: <strong>{suggestion.payment_method.name}</strong>.</>}
                            </p>

                            {installments.map((installment, index) => (
                                <div className={`order-billing-installment`} key={installment.key}>
                                    <span className={`installment-number`}>{index + 1}</span>
                                    <Input
                                        label={`Valor`}
                                        value={installment.amount}
                                        change={value => updateInstallment(installment.key, { amount: value })}
                                        mask={`convertToMoney`}
                                    />
                                    <InputDate
                                        label={`Vencimento`}
                                        format={`YYYY-MM-DD`}
                                        value={installment.due_date}
                                        change={value => updateInstallment(installment.key, { due_date: value })}
                                    />
                                    <button
                                        className={`installment-remove`}
                                        title={`Remover parcela`}
                                        onClick={() => removeInstallment(installment.key)}
                                        disabled={installments.length === 1}
                                    >
                                        <Icon name={`delete-small`} />
                                    </button>
                                </div>
                            ))}

                            <div className={`order-billing-installments-foot`}>
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    svg={`plus-blue`}
                                    text={`Adicionar parcela`}
                                    action={addInstallment}
                                />
                                <div className={`installments-total ${difference !== 0 ? 'diff' : ''}`}>
                                    <span>Total das parcelas: <strong>{convertIntToMoney(installmentsTotal, true)}</strong></span>
                                    <span>Total do pedido: <strong>{convertIntToMoney(suggestion.total, true)}</strong></span>
                                    {/* Diferença é aviso, não trava: parcelar com
                                        juros ou desconto negociado é legítimo. */}
                                    {difference !== 0 &&
                                        <span className={`installments-diff`}>
                                            Diferença de {convertIntToMoney(Math.abs(difference), true)}
                                            {difference > 0 ? ' a mais' : ' a menos'} que o pedido.
                                        </span>
                                    }
                                </div>
                            </div>
                        </>
                    }
                </div>
            </Modal>

            <div className={`order-billing`}>
                {canViewFinance &&
                    <div className={`order-billing-block`}>
                        <div className={`block-head`}>
                            <span>Títulos a receber</span>
                            {canManageFinance && activeEntries.length === 0 &&
                                <Button
                                    type={`primary`}
                                    size={`small`}
                                    svg={`plus-white`}
                                    text={`Gerar títulos`}
                                    action={openInstallments}
                                    loading={generating}
                                />
                            }
                            {activeEntries.length > 0 &&
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    svg={`external-link`}
                                    text={`Abrir no financeiro`}
                                    action={() => navigate(financeListUrl())}
                                />
                            }
                        </div>

                        {activeEntries.length === 0 && !loadingEntries &&
                            <p className={`order-billing-hint`}>
                                {pendingReason
                                    ? <>{pendingReason} Corrija na aba de detalhes do pedido ou gere os títulos informando as parcelas.</>
                                    : 'Este pedido ainda não gerou cobrança.'}
                            </p>
                        }

                        <Table
                            header={['Parcela', 'Descrição', 'Vencimento', 'Valor', 'Situação']}
                            rows={entryRows}
                            loading={loadingEntries}
                        />
                    </div>
                }

                {canViewPurchase &&
                    <div className={`order-billing-block`}>
                        <div className={`block-head`}>
                            <span>Pedidos de compra</span>
                            {canManagePurchase && activePurchaseOrders.length === 0 &&
                                <Button
                                    type={`primary`}
                                    size={`small`}
                                    svg={`plus-white`}
                                    text={`Gerar pedidos de compra`}
                                    action={generatePurchaseOrders}
                                    loading={generating}
                                />
                            }
                            {activePurchaseOrders.length > 0 &&
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    svg={`external-link`}
                                    text={`Abrir compras`}
                                    action={() => navigate(purchaseListUrl())}
                                />
                            }
                        </div>

                        {activePurchaseOrders.length === 0 && !loadingPurchases &&
                            <p className={`order-billing-hint`}>
                                Nenhuma compra gerada para este pedido. Sai um pedido de compra por
                                fornecedor dos itens — itens sem fornecedor definido ficam de fora.
                            </p>
                        }

                        <Table
                            header={['Código', 'Fornecedor', 'Tipo', 'Total', 'Situação']}
                            rows={purchaseRows}
                            loading={loadingPurchases}
                        />
                    </div>
                }
            </div>
        </>
    );
};

export default OrderFinanceTab;
